import React, { useEffect, useMemo, useRef, useState, KeyboardEvent } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import { ChevronLeft, Sparkles, ArrowUp, Loader2, X, Send } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import type { AppDispatch, RootState } from '@/redux'
import {
  sendChatMessage,
  confirmPanels,
} from '@/services/DashboardChatService'
import { sendEditChatMessageThunk } from '@/services/dashboardEditChatService'
import { chatSessionActions } from '@/redux/slice/chatSessionSlice'
import {
  dashboardViewActions,
  selectChatMode,
  selectEditingPanelTitle,
  selectPendingEditProposal,
  selectOutOfScopePrompt,
} from '@/redux/slice/dashboardViewSlice'
import {
  selectEditMessages,
  selectEditIsSending,
  selectEditSessionId,
  selectEditSessionDashboardId,
  selectIsBootstrapping,
} from '@/redux/slice/dashboardEditChatSlice'
import { renderTextWithLinks } from '@/services/utils/renderTextWithLinks'
import { StepProgressIndicator } from './StepProgressIndicator'
import { PanelProposalCheckCard } from './PanelProposalCheckCard'
import { DataSourceCard } from './DataSourceCard'
import { CredentialConfirmCard } from './CredentialConfirmCard'
import { FilterConfigCard } from './FilterConfigCard'
import { isRestoredTechnicalUserContent } from './userMessageDisplay'
import { PanelContextBanner } from './PanelContextBanner'
import { EditPanelProposalCard } from './EditPanelProposalCard'
import { OutOfScopeFilterAdjustPrompt } from './OutOfScopeFilterAdjustPrompt'
import type { OutOfScopePrompt } from '@/types/DashboardV1.types'
import { ConnectedThinDataDetectionMessage } from './ThinDataDetectionMessage'
import { LibraryGapBanner } from './LibraryGapBanner'
import { EditFilterCard } from './EditFilterCard'
import { QueryPanelDataTruncationBanner } from './QueryPanelDataTruncationBanner'
import { AggregateOtherBucketBanner } from './AggregateOtherBucketBanner'

interface ChatPanelProps {
  dashboardId: string
  totalSteps?: number
  /** Called after a successful send (HTTP 200 + Redux updated). Used on /dashboards/new to replace the URL with /dashboards/:id. */
  onSuccessfulChatSend?: () => void
  /**
   * creation — split layout with back link and step progress (dashboard build flow).
   * viewingDrawer — fixed-width overlay (viewing mode); requires onCloseDrawer.
   */
  variant?: 'creation' | 'viewingDrawer'
  /**
   * viewingDrawer variant only: whether the drawer is currently open.
   * Drives `aria-expanded` on the collapse (X) button so AT announces the
   * real drawer state rather than a hardcoded literal. Defaults to `true`
   * (the only time the close button is actionable is when the drawer is open,
   * but consumers that keep the drawer mounted off-screen should pass this
   * explicitly).
   */
  isOpen?: boolean
  /** Close control for viewingDrawer variant (mockup: X in header). */
  onCloseDrawer?: () => void
  /**
   * viewingDrawer variant only: called when user accepts scope expansion from
   * OutOfScopeFilterAdjustPrompt. Canvas handles filter expansion + re-fetch.
   */
  onExpandScope?: (prompt: OutOfScopePrompt) => void
  /**
   * viewingDrawer variant only: called on mount with a function that focuses
   * the chat textarea. Used by ChatOverlay to focus the input when the overlay
   * opens (brand guide §36 Focus management).
   */
  onFocusInputReady?: (focusFn: () => void) => void
  /**
   * When true, suppresses ChatPanel's own header chrome. Used when the parent
   * (ChatOverlay) renders its own v2 header so the two don't stack.
   */
  hideInternalHeader?: boolean
}

/**
 * ChatPanel — AI chat panel for creation (split layout) and viewing (side panel) modes.
 *
 * Viewing mode (viewingDrawer variant) extends with conversational edit (Track 2):
 *  - PanelContextBanner shown when chatMode === 'panel' (mockup 21, brand guide §32).
 *  - EditPanelProposalCard shown when a pendingEditProposal exists (mockup 18, §28).
 *  - OutOfScopeFilterAdjustPrompt shown when outOfScopePrompt is set (mockup 19, §28).
 *  - Input placeholder becomes "What would you like to change?" in panel edit mode.
 */
export const ChatPanel: React.FC<ChatPanelProps> = ({
  dashboardId,
  totalSteps,
  onSuccessfulChatSend,
  variant = 'creation',
  isOpen = true,
  onCloseDrawer,
  onExpandScope,
  onFocusInputReady,
  hideInternalHeader = false,
}) => {
  const dispatch: AppDispatch = useDispatch()
  const { token } = useSelector((state: RootState) => state.user)
  const {
    messages: creationMessages,
    creationStatus,
    isLoading,
    isSending: isCreationSending,
    selectedPanelIndices,
    error,
  } = useSelector((state: RootState) => state.chatSession)

  const isBuilding = useSelector((state: RootState) => state.dashboardPanels.isBuilding)

  // ── Conversational edit flow state (Track 2) ─────────────────────────────
  const chatMode = useSelector(selectChatMode)
  const editingPanelTitle = useSelector(selectEditingPanelTitle)
  const pendingEditProposal = useSelector(selectPendingEditProposal)
  const outOfScopePrompt = useSelector(selectOutOfScopePrompt)
  const isViewingDrawer = variant === 'viewingDrawer'
  const isPanelEditMode = isViewingDrawer && chatMode === 'panel'

  // ── Edit-mode chat thread (separate slice from create-mode chatSession) ──
  // The era axiom requires create-era and edit-era state to stay distinct,
  // so edit-mode messages live on dashboardEditChatSlice. We merge them
  // into the render-time `messages` array (create thread first, then edit
  // thread) so ChatPanel renders one continuous transcript.
  const editMessages = useSelector(selectEditMessages)
  const isEditSending = useSelector(selectEditIsSending)
  const editSessionId = useSelector(selectEditSessionId)
  const editSessionDashboardId = useSelector(selectEditSessionDashboardId)
  // isBootstrapping: true while the edit-session POST is in-flight. During
  // this window editSessionId is null (so isEditModeActive is false), which
  // would route a fast-typed user message to the create-mode /chat endpoint
  // instead of the edit endpoint. We include isBootstrapping in the send-gate
  // so the input is disabled for the brief bootstrap window. (Bug B follow-up.)
  const isEditSessionBootstrapping = useSelector(selectIsBootstrapping)
  // Edit mode is "active" when the overlay variant is in use AND the slice
  // holds a session bound to the dashboard we're viewing. This is what gates
  // edit-mode message dispatch — otherwise we'd send create-era chats to the
  // edit endpoint.
  const isEditModeActive =
    isViewingDrawer &&
    editSessionId !== null &&
    editSessionDashboardId === dashboardId
  // Memoise the merged messages array so consumers (useEffect dependency on
  // `messages`) don't see a fresh reference on every render.
  const messages = useMemo(
    () => (isEditModeActive ? [...creationMessages, ...editMessages] : creationMessages),
    [isEditModeActive, creationMessages, editMessages],
  )
  // In viewingDrawer mode: block sends while the edit session is bootstrapping
  // (session not yet open) OR while a message is in-flight. In creation mode:
  // use the creation-mode sending flag as before.
  const isSending = isViewingDrawer
    ? isEditSending || isEditSessionBootstrapping
    : isCreationSending

  const [inputText, setInputText] = useState('')
  const [filterCustomizeMode, setFilterCustomizeMode] = useState(false)
  const messagesScrollRef = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  // Register focus callback with parent overlay so it can focus the textarea
  // on overlay open (brand guide §36 Focus management). Stable: runs once on mount.
  useEffect(() => {
    if (onFocusInputReady) {
      onFocusInputReady(() => {
        textareaRef.current?.focus()
      })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Keep scroll inside the chat column only. scrollIntoView() on a sentinel walks ancestor
  // scrollers and can move the whole page / push the header out of view after large restores.
  useEffect(() => {
    const el = messagesScrollRef.current
    /* istanbul ignore next — ref is always attached to a mounted DOM node in tests; null path is a structural guard for SSR/unmount edge cases */
    if (!el) return
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        el.scrollTop = el.scrollHeight
      })
    })
  }, [messages, isLoading])

  const handleSend = async () => {
    const text = inputText.trim()
    if (!text || isSending || !token) return
    setInputText('')
    setFilterCustomizeMode(false)
    // Route to the edit-session messages endpoint when an edit session is
    // active on this dashboard. Otherwise fall through to the create-mode
    // chat endpoint. This is the era-distinct dispatch — Bug B fix.
    if (isEditModeActive) {
      const ok = await dispatch(sendEditChatMessageThunk(token, text))
      if (ok === true) onSuccessfulChatSend?.()
      return
    }
    const ok = await dispatch(sendChatMessage(token, dashboardId, text))
    if (ok === true) onSuccessfulChatSend?.()
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  const handleChoiceCardClick = async (cardText: string) => {
    if (isSending || !token) return
    setFilterCustomizeMode(false)
    // Same routing rule as handleSend — choice-card clicks in edit mode go
    // to the edit-session endpoint. Choice cards are a create-mode UI today,
    // so this branch is defensive; the legacy path remains the hot path.
    if (isEditModeActive) {
      const ok = await dispatch(sendEditChatMessageThunk(token, cardText))
      if (ok === true) onSuccessfulChatSend?.()
      return
    }
    const ok = await dispatch(sendChatMessage(token, dashboardId, cardText))
    if (ok === true) onSuccessfulChatSend?.()
  }

  const handleTogglePanel = (index: number) => {
    dispatch(chatSessionActions.toggleProposedPanel(index))
  }

  const handleConfirmPanels = () => {
    if (!token || selectedPanelIndices.length === 0) return
    dispatch(confirmPanels(token, dashboardId))
  }

  const noPanelsSelected = selectedPanelIndices.length === 0

  // Index of the last panel_proposals message — only that message renders interactive checkboxes.
  // Earlier proposal messages are rendered read-only to prevent stale index mapping.
  const lastProposalIndex = messages.reduce<number>(
    (last, msg, i) => (msg.type === 'panel_proposals' ? i : last),
    -1,
  )

  return (
    <div
      className={
        isViewingDrawer
          ? 'flex flex-col bg-background h-full min-h-0 overflow-hidden'
          : 'flex flex-col bg-background border-l border-border h-full min-h-0 overflow-hidden'
      }
    >
      {/* Header — creation split vs viewing overlay (mockup: drawer header + close).
          Suppressed when hideInternalHeader=true (ChatOverlay renders its own v2 header). */}
      {!hideInternalHeader && <div
        className={
          isViewingDrawer
            ? 'flex items-center gap-3 px-5 py-4 bg-background border-b border-border flex-shrink-0 z-10'
            : 'flex items-center gap-3.5 px-7 py-4 bg-background border-b border-border flex-shrink-0 z-10'
        }
      >
        {!isViewingDrawer && (
          <Link
            to="/dashboards"
            className="text-secondary hover:text-primary transition-colors flex-shrink-0"
            aria-label="Back to dashboards"
          >
            <ChevronLeft size={18} />
          </Link>
        )}

        <div
          className={
            isViewingDrawer
              ? 'w-8 h-8 rounded-full bg-gradient-to-br from-accent to-blue-700 flex items-center justify-center flex-shrink-0'
              : 'w-10 h-10 rounded-full bg-gradient-to-br from-accent to-blue-700 flex items-center justify-center flex-shrink-0 shadow-[0_0_0_3px_hsl(var(--accent-background))]'
          }
        >
          <Sparkles size={isViewingDrawer ? 14 : 18} className="text-white" />
        </div>

        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-primary leading-tight">TheCampStack AI Agent</p>
          <p className="text-xs text-secondary">
            {isViewingDrawer ? 'Ask questions about your dashboard' : 'Building your dashboard step by step'}
          </p>
        </div>

        {isViewingDrawer ? (
          <button
            type="button"
            className="w-8 h-8 flex items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-primary transition-colors flex-shrink-0"
            aria-label="Collapse chat panel"
            aria-expanded={isOpen}
            onClick={() => onCloseDrawer?.()}
          >
            <X size={16} aria-hidden="true" />
          </button>
        ) : (
          <StepProgressIndicator messages={messages} creationStatus={creationStatus} totalSteps={totalSteps} />
        )}
      </div>}

      {/* Panel context banner — shown when in panel-scoped edit mode (mockup 21) */}
      {isPanelEditMode && editingPanelTitle && (
        <PanelContextBanner panelTitle={editingPanelTitle} />
      )}

      {/* Messages */}
      <div
        ref={messagesScrollRef}
        className="flex-1 overflow-y-auto flex flex-col min-h-0 overscroll-y-contain"
        role="log"
        aria-live="polite"
        aria-label="Chat messages"
      >
        <div
          className={
            isViewingDrawer
              ? 'flex flex-col gap-5 px-5 py-5 w-full'
              : 'flex flex-col gap-3 py-9 w-full max-w-[736px] mx-auto px-7'
          }
        >
        {/* Error banner */}
        {error && (
          <div role="alert" className="bg-destructive-foreground border border-destructive rounded-lg px-4 py-3 text-sm text-destructive">
            {error}
          </div>
        )}

        {messages.map((msg, idx) => {
          if (msg.role === 'user') {
            // Strip credentialId suffix added by pill card clicks before displaying
            const displayContent = msg.content.replace(/\s*\[credentialId:[^\]]+\]$/, '')
            const isTechnical = isRestoredTechnicalUserContent(displayContent)
            return (
              <div key={idx} className="flex justify-end">
                <div
                  data-testid={isTechnical ? 'user-message-technical' : 'user-message'}
                  className={
                    isTechnical
                      ? 'rounded-2xl rounded-tr-sm px-3 py-2 text-sm max-w-[92%] border border-border bg-muted text-foreground shadow-sm'
                      : 'bg-accent text-foreground text-left rounded-2xl rounded-tr-sm px-4 py-3 text-sm max-w-[75%]'
                  }
                >
                  {isTechnical ? (
                    <>
                      <p className="text-[11px] font-medium text-secondary mb-1.5">
                        Restored selection (details)
                      </p>
                      <div className="max-h-[min(40vh,22rem)] overflow-y-auto font-mono text-[11px] leading-relaxed whitespace-pre-wrap break-words">
                        {displayContent}
                      </div>
                    </>
                  ) : (
                    displayContent
                  )}
                </div>
              </div>
            )
          }

          // Assistant message
          return (
            <div key={idx} className="flex gap-3 items-start">
              <div className="w-8 h-8 rounded-full bg-accent flex items-center justify-center flex-shrink-0">
                <Sparkles size={14} className="text-foreground" />
              </div>
              <div className="flex-1 min-w-0">
                {/* Choice cards — text + cards rendered inside one unified bubble */}
                {msg.type === 'choice_cards' && msg.choiceCards && (() => {
                  const isLocked = idx < messages.length - 1
                  const nextUserContent = messages[idx + 1]?.content ?? null
                  const variant = msg.choiceCardVariant ?? 'pill'

                  const cards = (() => {
                    if (variant === 'source_card') {
                      return (
                        <div className="grid grid-cols-2 gap-2 mt-4">
                          {msg.choiceCards!.map((card) => (
                            <DataSourceCard
                              key={card.id}
                              sourceName={card.label}
                              selected={isLocked && nextUserContent === card.label}
                              locked={isLocked}
                              onSelect={() => handleChoiceCardClick(card.label)}
                            />
                          ))}
                        </div>
                      )
                    }

                    // Default: pill variant
                    // For pills, encode the card id in the sent message so the AI receives the
                    // credentialId directly (avoids AI having to guess UUID from nickname text).
                    // Format: "Nickname [credentialId: <uuid>]" — backend system prompt parses this.
                    return (
                      <div className="flex flex-wrap gap-2 mt-4">
                        {msg.choiceCards!.map((card) => {
                          const sentValue = `${card.label} [credentialId: ${card.id}]`
                          const isSelected =
                            isLocked &&
                            (nextUserContent === card.label ||
                              nextUserContent === sentValue)
                          return (
                            <Button
                              key={card.id}
                              type="button"
                              variant="outline"
                              className={
                                isLocked
                                  ? isSelected
                                    ? 'h-auto px-4 py-2 rounded-md text-sm font-medium border-accent bg-accent-background text-accent cursor-default pointer-events-none hover:bg-accent-background hover:text-accent'
                                    : 'h-auto px-4 py-2 rounded-md text-sm font-medium border-border bg-card text-secondary cursor-default pointer-events-none hover:bg-card'
                                  : 'h-auto px-4 py-2 rounded-md border-border bg-card text-sm font-medium text-primary hover:bg-accent-background hover:border-accent transition-colors duration-150'
                              }
                              onClick={() => handleChoiceCardClick(sentValue)}
                              disabled={isSending || isLocked}
                            >
                              {card.label}
                            </Button>
                          )
                        })}
                      </div>
                    )
                  })()

                  return (
                    <div className="bg-card border border-border rounded-2xl rounded-tl-sm px-5 py-4 text-sm text-left text-primary max-w-full mb-2 shadow-[0_1px_4px_rgba(15,23,42,0.06)]">
                      {msg.content && <p className="leading-relaxed">{renderTextWithLinks(msg.content)}</p>}
                      {cards}
                    </div>
                  )
                })()}

                {/* Message text bubble — for non-choice_cards, non-confirmation types.
                    `query_panel_data_result` renders AI prose + truncation banner (Slice 2.2).
                    `aggregate_panel_data_result` renders AI prose + optional other-bucket banner
                    when grouped result has hasOtherBucket=true (Slice 3.2). Scalar aggregate
                    results render prose only — no banner needed. */}
                {msg.content && msg.type !== 'confirmation' && msg.type !== 'choice_cards' && (
                  <div className="bg-card border border-border rounded-2xl rounded-tl-sm px-5 py-4 text-sm text-left text-primary max-w-full mb-2 shadow-[0_1px_4px_rgba(15,23,42,0.06)]">
                    {renderTextWithLinks(msg.content)}
                    {/* Truncation banner — rendered inside the bubble below the AI prose
                        when query_panel_data returned truncated:true. The → hint is prose
                        only, not a clickable CTA (TDD §3.7 / CEO OQ-5: verbal "yes" is
                        the first gate, tool-card confirm is the second).
                        Narrowed on tool === 'query_panel_data' so TS resolves the
                        QueryPanelDataToolResult discriminated member. */}
                    {msg.type === 'query_panel_data_result' && msg.qaToolResult?.tool === 'query_panel_data' && (
                      <QueryPanelDataTruncationBanner qaToolResult={msg.qaToolResult} />
                    )}
                    {/* Other-bucket banner — rendered inside the bubble below the AI prose
                        when aggregate_panel_data returned a grouped result with hasOtherBucket=true.
                        Scalar aggregate results (kind==='scalar') render prose only — no banner.
                        Track 3 Slice 3.2 / TDD §3.7. */}
                    {msg.type === 'aggregate_panel_data_result' && msg.qaToolResult?.tool === 'aggregate_panel_data' && (
                      <AggregateOtherBucketBanner qaToolResult={msg.qaToolResult} />
                    )}
                  </div>
                )}

                {/* Panel proposals — wrapper fires when there are panels OR a libraryGap
                    so the banner is never suppressed by an empty panel list (F1 fix). */}
                {msg.type === 'panel_proposals' && (msg.libraryGap != null || (msg.panels && msg.panels.length > 0)) && (() => {
                  const isActiveProposal =
                    creationStatus === 'awaiting_confirmation' && idx === lastProposalIndex
                  return (
                    <div className="mt-2 max-w-full">
                      {/* Library gap banner — TDD §4.8.3. Rendered above the panel list so
                          the user sees the honest gap acknowledgment before reviewing proposals.
                          Outer wrapper fires on libraryGap OR panels so this banner is never
                          suppressed by an empty-panel message. */}
                      {msg.libraryGap != null && (
                        <LibraryGapBanner libraryGap={msg.libraryGap} />
                      )}

                      {/* Panel list — only when there are panels to show */}
                      {msg.panels && msg.panels.length > 0 && (
                        <>
                          <div className="border border-border rounded-xl overflow-hidden bg-card divide-y divide-border mb-4">
                            {msg.panels.map((panel, panelIdx) => (
                              <PanelProposalCheckCard
                                key={panelIdx}
                                panel={panel}
                                panelIndex={panelIdx}
                                isSelected={selectedPanelIndices.includes(panelIdx)}
                                onToggle={handleTogglePanel}
                                readOnly={!isActiveProposal}
                              />
                            ))}
                          </div>

                          {/* Build My Dashboard — only while awaiting confirmation on the latest proposal */}
                          {isActiveProposal && (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className="block w-full">
                                    <Button
                                      className="w-full font-semibold"
                                      onClick={handleConfirmPanels}
                                      disabled={noPanelsSelected || isBuilding}
                                      aria-disabled={noPanelsSelected || isBuilding}
                                      aria-label={isBuilding ? 'Building dashboard, please wait' : 'Build My Dashboard'}
                                    >
                                      {isBuilding
                                        ? <><Loader2 size={16} className="animate-spin mr-2" aria-hidden="true" />Building...</>
                                        : 'Build My Dashboard'
                                      }
                                    </Button>
                                  </span>
                                </TooltipTrigger>
                                {noPanelsSelected && (
                                  <TooltipContent>
                                    <p>Select at least one panel to continue.</p>
                                  </TooltipContent>
                                )}
                              </Tooltip>
                            </TooltipProvider>
                          )}
                        </>
                      )}
                    </div>
                  )
                })()}

                {/* Credential confirmation card */}
                {msg.type === 'credential_card' && msg.appName && msg.credentialName && msg.credentialType && (() => {
                  const isLocked = idx < messages.length - 1
                  return (
                    <CredentialConfirmCard
                      appName={msg.appName}
                      credentialName={msg.credentialName}
                      logoUrl={msg.logoUrl ?? ''}
                      credentialType={msg.credentialType}
                      expiresAt={msg.expiresAt ?? null}
                      status={msg.status ?? 'active'}
                      locked={isLocked}
                      onConfirm={() => handleChoiceCardClick('Yes, use this credential')}
                      onUseDifferent={() => handleChoiceCardClick('Use a different one')}
                    />
                  )
                })()}

                {/* Filter configuration card (create-mode, workflow-era) */}
                {msg.type === 'filter_card' && Array.isArray(msg.filters) && (() => {
                  const isLocked = idx < messages.length - 1
                  return (
                    <FilterConfigCard
                      filters={msg.filters}
                      locked={isLocked}
                      onConfirm={(confirmedFilters) => {
                        const filterSummary = confirmedFilters
                          .map(f => `${f.label}: ${Array.isArray(f.value) ? f.value.join(', ') : f.value}`)
                          .join(' | ')
                        handleChoiceCardClick(`These look right → Continue (${filterSummary})`)
                      }}
                      onCustomize={() => {
                        setFilterCustomizeMode(true)
                        setTimeout(() => textareaRef.current?.focus(), 0)
                      }}
                    />
                  )
                })()}

                {/* Edit-mode filter card (dashboard-era, Epic 3 §7.2)
                    Renders when an edit-session response has type 'edit_filter_card'.
                    Distinct from 'filter_card' — no workflow-era fields.
                    Locked when a newer assistant turn has arrived (idx < last message). */}
                {msg.type === 'edit_filter_card' && Array.isArray(msg.editFilterFields) && (() => {
                  const isLocked = idx < messages.length - 1
                  return (
                    <EditFilterCard
                      fields={msg.editFilterFields}
                      locked={isLocked}
                    />
                  )
                })()}

                {/* Confirmation message */}
                {msg.type === 'confirmation' && (
                  <div className="bg-card border border-border rounded-2xl rounded-tl-sm px-5 py-4 text-sm text-left text-primary max-w-full shadow-[0_1px_4px_rgba(15,23,42,0.06)]">
                    {msg.content}
                  </div>
                )}

                {/* Timestamp (only when non-empty) */}
                {msg.timestamp && (
                  <p className="text-xs text-secondary mt-1 pl-1">
                    {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </p>
                )}
              </div>
            </div>
          )
        })}

        {/* Out-of-scope prompt — rendered after messages.map so it appears as
            the latest AI response (below the user's triggering message), not
            above the chat history. Per mockup 19 the callout is the most
            recent assistant bubble in the conversation.

            Fallback: if the parent has not provided `onExpandScope` (e.g.
            during creation mode where the prompt shouldn't surface anyway),
            we dispatch `expandScope` locally so the user is never stuck with
            a prompt that silently no-ops. */}
        {isViewingDrawer && outOfScopePrompt && (
          <div className="flex gap-3 items-start">
            <div className="w-8 h-8 rounded-full bg-accent flex items-center justify-center flex-shrink-0" aria-hidden="true">
              <Sparkles size={14} className="text-accent-foreground" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="bg-card border border-border rounded-2xl rounded-tl-sm px-5 py-4 text-sm text-primary max-w-full shadow-[0_1px_4px_rgba(15,23,42,0.06)]">
                <OutOfScopeFilterAdjustPrompt
                  prompt={outOfScopePrompt}
                  onExpandScope={(prompt) => {
                    if (onExpandScope) {
                      onExpandScope(prompt)
                    } else {
                      // Fallback so the CTA is never a silent no-op.
                      dispatch(dashboardViewActions.expandScope())
                    }
                  }}
                />
              </div>
            </div>
          </div>
        )}

        {/* Pending edit proposal card — rendered after messages.map so it
            appears as the latest AI response, inline with the current turn
            (mockup 18). */}
        {isViewingDrawer && pendingEditProposal && (
          <div className="flex gap-3 items-start">
            <div className="w-8 h-8 rounded-full bg-accent flex items-center justify-center flex-shrink-0" aria-hidden="true">
              <Sparkles size={14} className="text-accent-foreground" />
            </div>
            <div className="flex-1 min-w-0">
              <EditPanelProposalCard
                proposal={pendingEditProposal}
                onAccept={() => dispatch(dashboardViewActions.acceptEdit())}
                onDismiss={() => dispatch(dashboardViewActions.dismissEdit())}
              />
            </div>
          </div>
        )}

        {/* Thin-data detection message — §3d.5 / brand-guide §24
          * Rendered as an AI bubble after the panel proposals when the backend flagged
          * < 20 total rows across all domains. Clears when user acts on a suggestion.
          * isSending is threaded through so send-bound chips can't silently drop their
          * payload while a previous send is still in-flight (see ThinDataDetectionMessage). */}
        <ConnectedThinDataDetectionMessage
          isSending={isSending}
          onSendSuggestion={(text) => {
            if (!isSending && token) {
              void dispatch(sendChatMessage(token, dashboardId, text))
            }
          }}
        />

        {/* Fetching data status message */}
        {creationStatus === 'fetching_data' && (
          <div className="flex gap-3 items-start">
            <div className="w-8 h-8 rounded-full bg-accent flex items-center justify-center flex-shrink-0">
              <Sparkles size={14} className="text-accent-foreground" />
            </div>
            <div className="bg-card border border-border rounded-2xl rounded-tl-sm px-5 py-4 text-sm text-primary max-w-full shadow-[0_1px_4px_rgba(15,23,42,0.06)] flex items-center gap-2">
              <Loader2 size={14} className="animate-spin flex-shrink-0 text-muted-foreground" />
              <span>
                Fetching your data — this may take a minute. You can leave and come back, or wait here.
              </span>
            </div>
          </div>
        )}

        {/* AI typing indicator.
         *
         * Announcement strategy (TDD Open Question 3 fallback):
         * The parent messages container already has role="log" + aria-live="polite".
         * Rather than nesting a second aria-live region, this wrapper carries NO live-region
         * attributes — it is announced by the parent role="log" when it mounts into the DOM,
         * and the sr-only <span> provides the accessible text content ("AI is typing") for
         * assistive technology to read. The visual avatar and dots are aria-hidden so they
         * don't contribute duplicate or empty announcements.
         *
         * This avoids the reliability issues of (a) a live region with no text content
         * (aria-label on a live region is inconsistently supported) and (b) nested live regions.
         */}
        {(isLoading || isSending) && creationStatus !== 'fetching_data' && (
          <div className="flex gap-3 items-start" data-testid="typing-indicator">
            <span className="sr-only">AI is typing</span>
            <div className="w-8 h-8 rounded-full bg-accent flex items-center justify-center flex-shrink-0" aria-hidden="true">
              <Sparkles size={14} className="text-foreground" />
            </div>
            <div className="flex gap-1 p-3 bg-foreground rounded-lg rounded-tl-none" aria-hidden="true">
                <span className="w-2 h-2 rounded-full bg-[#BFDBFE] motion-safe:animate-bounce [animation-delay:0ms]" />
              <span className="w-2 h-2 rounded-full bg-[#BFDBFE] motion-safe:animate-bounce [animation-delay:150ms]" />
              <span className="w-2 h-2 rounded-full bg-[#BFDBFE] motion-safe:animate-bounce [animation-delay:300ms]" />
            </div>
          </div>
        )}
        </div>
      </div>

      {/* Input bar */}
      <div
        className={
          isViewingDrawer
            ? 'border-t border-border bg-background px-3 py-3 flex-shrink-0'
            : 'border-t border-border bg-background px-7 py-4 pb-5 flex-shrink-0'
        }
      >
        {isViewingDrawer ? (
          <div className="flex items-end gap-2 bg-background">
            <Textarea
              ref={textareaRef}
              placeholder={isPanelEditMode ? 'What would you like to change?' : 'Ask about your dashboard data...'}
              className="flex-1 border border-border rounded-md text-sm text-primary resize-none h-10 max-h-[120px] px-3 py-2 bg-card focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-accent"
              rows={1}
              value={inputText}
              onChange={e => setInputText(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={(isSending || creationStatus === 'fetching_data') && !filterCustomizeMode}
              aria-label={isPanelEditMode ? 'Describe your panel edit' : 'Message TheCampStack AI Agent'}
            />
            <Button
              size="icon"
              className="h-10 w-10 rounded-lg flex-shrink-0 bg-gradient-to-br from-accent to-blue-700 hover:opacity-90"
              aria-label="Send message"
              onClick={handleSend}
              disabled={!inputText.trim() || ((isSending || creationStatus === 'fetching_data') && !filterCustomizeMode)}
            >
              <Send size={16} className="text-white" />
            </Button>
          </div>
        ) : (
          <>
            <div className="max-w-[680px] mx-auto flex items-end gap-2.5 bg-slate-50 border border-border rounded-2xl px-4 py-2.5 focus-within:border-accent focus-within:shadow-[0_0_0_3px_rgba(59,130,246,0.10)] transition-shadow">
              <Textarea
                ref={textareaRef}
                placeholder="Message the AI..."
                className="resize-none flex-1 text-sm text-primary bg-transparent border-none shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 h-auto min-h-[22px] max-h-[120px] p-0 leading-relaxed placeholder:text-muted-foreground"
                rows={1}
                value={inputText}
                onChange={e => setInputText(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={(isSending || creationStatus === 'fetching_data') && !filterCustomizeMode}
                aria-label="Chat input"
              />
              <Button
                size="icon"
                className="h-9 w-9 rounded-xl flex-shrink-0 mb-0.5"
                aria-label="Send message"
                onClick={handleSend}
                disabled={!inputText.trim() || ((isSending || creationStatus === 'fetching_data') && !filterCustomizeMode)}
              >
                <ArrowUp size={16} />
              </Button>
            </div>
            <p className="text-center text-xs text-muted-foreground mt-2 max-w-[680px] mx-auto">
              TheCampStack AI Agent can make mistakes. Review important data before building.
            </p>
          </>
        )}
      </div>
    </div>
  )
}
